import { useState, useEffect } from 'react';
import axios from 'axios';
import { useWorkspace } from '../../context/WorkspaceContext';
import { RiBrainLine } from 'react-icons/ri';
import { MdImage } from 'react-icons/md';
import { BiUpload } from 'react-icons/bi';
import Progress from '../../ui/progress'; // Assuming you have a Progress component in ui folder

// Custom CircularProgressbar component to avoid dependency on react-circular-progressbar
const CircularProgressbar = ({ value, text, strokeWidth = 8, styles = {}, color = '#10b981' }) => {
  // Calculate the path parameters
  const radius = 50 - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (value / 100) * circumference;

  // Default styles
  const defaultStyles = {
    path: {
      stroke: color,
      strokeLinecap: 'round',
      transition: 'stroke-dashoffset 0.5s ease 0s',
    },
    trail: {
      stroke: '#e5e7eb', // gray-200
    },
    text: {
      fill: '#1f2937', // gray-800
      fontSize: '16px',
      dominantBaseline: 'middle',
      textAnchor: 'middle',
    },
    background: {
      fill: '#fff',
    },
  };

  // Merge default styles with custom styles
  const mergedStyles = {
    path: { ...defaultStyles.path, ...styles.path },
    trail: { ...defaultStyles.trail, ...styles.trail },
    text: { ...defaultStyles.text, ...styles.text },
    background: { ...defaultStyles.background, ...styles.background },
  };

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <svg viewBox="0 0 100 100" width="100%" height="100%">
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          style={mergedStyles.background}
        />
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={mergedStyles.trail.stroke}
          strokeWidth={strokeWidth}
        />
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          stroke={mergedStyles.path.stroke}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap={mergedStyles.path.strokeLinecap}
          style={{ transition: mergedStyles.path.transition }}
        />
        <text x="50" y="50" style={mergedStyles.text}>
          {text}
        </text>
      </svg>
    </div>
  );
};

const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL;

export default function Usage({ 
  type = 'quota', // 'quota', 'storage', 'combined'
  showGpt = true, 
  showImage = false, 
  showUpload = false,
  showLabels = false,
  variant = 'pill', // 'compact', 'full', 'pill'
  showStorageBreakdown = false,
  className = '',
  onDataFetched = null,
  refreshTrigger = null,
  fixed = false
}) {
  const [quotaData, setQuotaData] = useState(null);
  const [storageData, setStorageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { idToken, activeWorkspace, workspaces } = useWorkspace();
  const [opacity, setOpacity] = useState(1);

  // Handle scroll effect for fixed positioning
  useEffect(() => {
    if (!fixed) return;
    
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const maxScroll = 200;
      const newOpacity = Math.max(0.5, 1 - scrollTop / maxScroll);
      setOpacity(newOpacity);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [fixed]);

  // Fetch data on component mount
  useEffect(() => {
    if (idToken) {
      fetchData();
    }
  }, [idToken, activeWorkspace]);
  
  // Refresh data when refreshTrigger changes
  useEffect(() => {
    if (idToken && refreshTrigger !== null) {
      fetchData();
    }
  }, [refreshTrigger, idToken]);

  // Function to fetch all usage information
  const fetchData = async () => {
    if (!idToken) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch quota data (AI, uploads, etc)
      if (type === 'quota' || type === 'combined') {
        await fetchQuotaData();
      }
      
      // Fetch storage data
      if (type === 'storage' || type === 'combined') {
        await fetchStorageData();
      }
      
      // Call the callback if provided
      if (onDataFetched) {
        const combinedData = {
          ...(quotaData && { quota: quotaData }),
          ...(storageData && { storage: storageData })
        };
        onDataFetched(combinedData);
      }
    } catch (err) {
      console.error('Error fetching usage data:', err);
      setError('Failed to load usage information');
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch quota information
  const fetchQuotaData = async () => {
    try {
      // Fetch both GPT and image quotas with a single API call
      if (showGpt || showImage) {
        try {
          const quotaResponse = await axios.get(`${BACKEND_API_URL}/api/quota/ai-quota`, {
            headers: { Authorization: `Bearer ${idToken}` }
          });
          
          if (quotaResponse.data.success) {
            const newQuotaData = {};
            
            // Process GPT quota
            if (showGpt) {
              const gptQuota = quotaResponse.data.quota;
              newQuotaData.gpt = {
                plan: gptQuota.plan,
                limit: gptQuota.limit,
                used: gptQuota.used,
                remaining: gptQuota.remaining,
                percentage: gptQuota.unlimited ? 100 : Math.round((gptQuota.used / gptQuota.limit) * 100),
                unlimited: gptQuota.unlimited
              };
            }
            
            // Process image quota
            if (showImage) {
              const imageQuota = quotaResponse.data.imageQuota;
              newQuotaData.image = {
                plan: imageQuota.plan,
                limit: imageQuota.limit,
                used: imageQuota.used,
                remaining: imageQuota.remaining,
                percentage: imageQuota.unlimited ? 100 : Math.round((imageQuota.used / imageQuota.limit) * 100),
                unlimited: imageQuota.unlimited
              };
            }
            
            setQuotaData(prevData => ({
              ...prevData,
              ...newQuotaData
            }));
          }
        } catch (err) {
          console.error('Error fetching AI quota:', err);
          
          // If showing GPT and there was an error, add fallback
          if (showGpt && !quotaData?.gpt) {
            setQuotaData(prevData => ({
              ...prevData,
              gpt: {
                plan: 'free',
                limit: 50,
                used: 0,
                remaining: 50,
                percentage: 0,
                unlimited: false
              }
            }));
          }
          
          // If showing image and there was an error, add fallback
          if (showImage && !quotaData?.image) {
            setQuotaData(prevData => ({
              ...prevData,
              image: {
                plan: 'free',
                limit: 5,
                used: 0,
                remaining: 5,
                percentage: 0,
                unlimited: false
              }
            }));
          }
        }
      }
      
      // Fetch upload quota
      if (showUpload) {
        const uploadResponse = await axios.get(`${BACKEND_API_URL}/api/quota/upload-quota`, {
          headers: { Authorization: `Bearer ${idToken}` }
        });
        
        if (uploadResponse.data) {
          const { uploadCount, limit, percentage, plan } = uploadResponse.data;
          
          setQuotaData(prevData => ({
            ...prevData,
            upload: {
              used: uploadCount,
              limit: limit,
              remaining: limit - uploadCount,
              percentage: Math.round(percentage),
              plan: plan,
              unlimited: limit === 'Unlimited'
            }
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching quota data:', error);
      throw error;
    }
  };

  // Function to fetch storage usage information
  const fetchStorageData = async () => {
    try {
      const response = await axios.get(`${BACKEND_API_URL}/api/quota/storage`, {
        headers: { Authorization: `Bearer ${idToken}` },
        params: { workspaceId: activeWorkspace.id }
      });
      
      if (response.data) {
        const { imagesSize, videosSize, totalSize, storageLimit } = response.data;
        
        // Format sizes
        const mbBytes = 1048576; // 1024 * 1024 bytes = 1 MB
        const formattedImageSize = formatBytes(imagesSize, 2);
        const formattedVideoSize = formatBytes(videosSize, 2);
        const formattedTotalSize = formatBytes(totalSize, 2);
        const formattedLimit = `${storageLimit} MB`;
        
        // Calculate percentage
        const storagePercentage = storageLimit > 0 ? Math.round((totalSize / (storageLimit * mbBytes)) * 100) : 0;
        
        setStorageData({
          total: formattedTotalSize,
          images: formattedImageSize,
          videos: formattedVideoSize,
          limit: formattedLimit,
          percentage: storagePercentage,
          rawTotal: totalSize,
          rawLimit: storageLimit * mbBytes
        });
      }
    } catch (error) {
      console.error('Error fetching storage data:', error);
      throw error;
    }
  };

  // Helper function to format bytes
  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  // Render a single quota item
  const renderQuotaItem = (type, icon, data, color) => {
    if (!data) return null;
    
    // Different display variants
    if (variant === 'compact') {
      return (
        <div className="flex items-center mr-2 --theme-colors" key={type}>
          <span className="mr-1">{icon}</span>
          <span className="text-xs">
            {data.unlimited ? '∞' : data.remaining}
          </span>
        </div>
      );
    } else if (variant === 'pill') {
      return (
        <div className="flex items-center space-x-1 --theme-colors py-1 px-3 rounded-full mr-2" key={type}>
          {icon}
          <span className="text-xs font-medium">
            {data.unlimited ? (
              'Unlimited'
            ) : (
              <>
                {data.remaining} / {data.limit}
                {showLabels && <span className="ml-1 text-gray-400 text-xs">{type}</span>}
              </>
            )}
          </span>
        </div>
      );
    } else if (variant === 'progress') {
      // Progress bar variant
      return (
        <div className="mb-4" key={type}>
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center">
              {icon}
              <span className="ml-2 font-medium">{type.charAt(0).toUpperCase() + type.slice(1)} Credits</span>
            </div>
            <span className="text-sm text-gray-500">
              {data.unlimited ? 'Unlimited' : `${data.used} / ${data.limit}`}
            </span>
          </div>
          <Progress 
            value={data.unlimited ? 100 : data.percentage} 
            color={color || (type === 'gpt' ? '#3b82f6' : type === 'image' ? '#10b981' : '#8b5cf6')}
          />
          {data.percentage >= 100 && !data.unlimited && (
            <p className="mt-1 text-red-600 text-xs">
              You've reached your maximum {type} usage limit!
            </p>
          )}
          {data.percentage > 80 && data.percentage < 100 && !data.unlimited && (
            <p className="mt-1 text-red-600 text-xs">
              You're approaching your {type} usage limit!
            </p>
          )}
        </div>
      );
    } else {
      // Full variant - uses circular progress instead of bar
      return (
        <div className="flex flex-col items-center mb-4" key={type}>
          <div className="w-24 h-24 mb-2">
            <CircularProgressbar 
              value={data.unlimited ? 100 : data.percentage} 
              text={data.unlimited ? '∞' : `${data.percentage}%`} 
              color={color || (type === 'gpt' ? '#3b82f6' : type === 'image' ? '#10b981' : '#8b5cf6')}
            />
          </div>
          <div className="text-center">
            <p className="font-medium">{type.charAt(0).toUpperCase() + type.slice(1)} Credits</p>
            <p className="text-sm text-gray-500">
              {data.unlimited ? 'Unlimited' : `${data.used} / ${data.limit} used`}
            </p>
          </div>
        </div>
      );
    }
  };

  // Render storage usage with breakdown option
  const renderStorageUsage = () => {
    if (!storageData) return null;

    if (variant === 'pill') {
      return (
        <div className="flex items-center space-x-1 --theme-colors py-1 px-3 rounded-full">
          <svg className="h-4 w-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <span className="text-xs font-medium">
            {storageData.total} / {storageData.limit}
          </span>
        </div>
      );
    } else if (variant === 'progress') {
      return (
        <div className="mb-4">
          <div className="flex justify-between items-center mb-2">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"></path>
              </svg>
              <span className="ml-2 font-medium">Storage</span>
            </div>
            <span className="text-sm text-gray-500">
              {storageData.total} / {storageData.limit}
            </span>
          </div>
          <Progress value={storageData.percentage} color="#3b82f6" />
          {showStorageBreakdown && (
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div className="flex items-center text-sm">
                <div className="w-3 h-3 rounded-full bg-blue-400 mr-2"></div>
                <span>Images: {storageData.images}</span>
              </div>
              <div className="flex items-center text-sm">
                <div className="w-3 h-3 rounded-full bg-purple-400 mr-2"></div>
                <span>Videos: {storageData.videos}</span>
              </div>
            </div>
          )}
          {storageData.percentage >= 100 && (
            <p className="mt-1 text-red-600 text-xs">
              You've reached your maximum storage limit!
            </p>
          )}
          {storageData.percentage > 80 && storageData.percentage < 100 && (
            <p className="mt-1 text-red-600 text-xs">
              You're approaching your storage limit!
            </p>
          )}
        </div>
      );
    } else {
      // Full variant
      return (
        <div className="flex flex-col items-center mb-4">
          <div className="w-24 h-24 mb-2">
            <CircularProgressbar 
              value={storageData.percentage} 
              text={`${storageData.percentage}%`} 
              color="#3b82f6"
            />
          </div>
          <div className="text-center">
            <p className="font-medium">Storage</p>
            <p className="text-sm text-gray-500">
              {storageData.total} / {storageData.limit}
            </p>
          </div>
          {showStorageBreakdown && (
            <div className="grid grid-cols-2 gap-4 mt-2 w-full">
              <div className="text-center">
                <p className="text-xs text-gray-500">Images</p>
                <p className="font-medium text-sm">{storageData.images}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500">Videos</p>
                <p className="font-medium text-sm">{storageData.videos}</p>
              </div>
            </div>
          )}
        </div>
      );
    }
  };

  // Loading state
  if (loading && !quotaData && !storageData) {
    return (
      <div className={`flex items-center ${className}`}>
        <div className="flex items-center space-x-1">
          <RiBrainLine className="h-4 w-4 text-gray-400" />
          <span className="text-xs text-gray-400">Loading...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !quotaData && !storageData) {
    return (
      <div className={`flex items-center ${className}`}>
        <div className="text-xs text-red-500">
          {error}
        </div>
      </div>
    );
  }

  // If no data is available
  if ((!quotaData && type !== 'storage') && (!storageData && type !== 'quota')) {
    return null;
  }

  // Statistics Card variant - for GPT & Image credits
  if (variant === 'statistics' || variant === 'progress') {
    return (
      <div className={className} style={{ opacity }}>
        {type === 'quota' && (
          <>
            {showGpt && quotaData?.gpt && renderQuotaItem('gpt', <RiBrainLine className="h-5 w-5 text-blue-500" />, quotaData.gpt, '#3b82f6')}
            {showImage && quotaData?.image && renderQuotaItem('image', <MdImage className="h-5 w-5 text-green-500" />, quotaData.image, '#10b981')}
            {showUpload && quotaData?.upload && renderQuotaItem('upload', <BiUpload className="h-5 w-5 text-purple-500" />, quotaData.upload, '#8b5cf6')}
          </>
        )}
        
        {type === 'storage' && renderStorageUsage()}
        
        {type === 'combined' && (
          <>
            {showGpt && quotaData?.gpt && renderQuotaItem('gpt', <RiBrainLine className="h-5 w-5 text-blue-500" />, quotaData.gpt, '#3b82f6')}
            {showImage && quotaData?.image && renderQuotaItem('image', <MdImage className="h-5 w-5 text-green-500" />, quotaData.image, '#10b981')}
            {showUpload && quotaData?.upload && renderQuotaItem('upload', <BiUpload className="h-5 w-5 text-purple-500" />, quotaData.upload, '#8b5cf6')}
            {storageData && renderStorageUsage()}
          </>
        )}
      </div>
    );
  }

  // Default display
  return (
    <div className={`flex ${variant === 'full' ? 'flex-col' : 'flex-row'} ${className}`} style={{ opacity }}>
      {type === 'quota' && (
        <>
          {showGpt && quotaData?.gpt && renderQuotaItem('gpt', <RiBrainLine className="h-4 w-4 text-blue-500" />, quotaData.gpt)}
          {showImage && quotaData?.image && renderQuotaItem('image', <MdImage className="h-4 w-4 text-green-500" />, quotaData.image)}
          {showUpload && quotaData?.upload && renderQuotaItem('upload', <BiUpload className="h-4 w-4 text-purple-500" />, quotaData.upload)}
        </>
      )}
      
      {type === 'storage' && renderStorageUsage()}
      
      {type === 'combined' && (
        <>
          {showGpt && quotaData?.gpt && renderQuotaItem('gpt', <RiBrainLine className="h-4 w-4 text-blue-500" />, quotaData.gpt)}
          {showImage && quotaData?.image && renderQuotaItem('image', <MdImage className="h-4 w-4 text-green-500" />, quotaData.image)}
          {showUpload && quotaData?.upload && renderQuotaItem('upload', <BiUpload className="h-4 w-4 text-purple-500" />, quotaData.upload)}
          {storageData && renderStorageUsage()}
        </>
      )}
    </div>
  );
} 